import { TrendingDown, TrendingUp } from 'lucide-react';

import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { Chart } from '@/components/ui/chart';
import { generateChartConfig } from '@/components/ui/chart/utils';
import { LocationModel } from '@/lib/models/location/types';
import { formatMarketRateModelData } from '@/lib/models/rates/format-market-rate-data';
import { MarketRateModel } from '@/lib/models/rates/types';
import { SegmentModeType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { getLocationName } from '@/utils/locations';
import { tv } from '@/utils/styles';

import { RatesRouteBadge } from '../route-badge';

interface RatesMarketRateDetailsProps {
  rates?: MarketRateModel;
  mode: SegmentModeType;
  origin?: LocationModel;
  destination?: LocationModel;
  customLabel?: string;
  shouldShowFooter?: boolean;
  shouldShowChart?: boolean;
  shouldShowChartLegend?: boolean;
  variant?: 'sm' | 'md' | 'lg';
}

const RatesMarketRateDetails = ({
  rates,
  mode,
  origin,
  destination,
  customLabel,
  shouldShowFooter,
  shouldShowChart = true,
  shouldShowChartLegend,
  variant = 'sm',
}: RatesMarketRateDetailsProps) => {
  const isMiniChart = variant === 'sm';
  const isLargeTile = variant === 'lg';

  // Locations
  const hasLocations = origin && destination;
  const originPortId = getPreferredIdentifier(origin?.identifiers);
  const destinationPortId = getPreferredIdentifier(destination?.identifiers);

  const {
    priceString,
    percentString,
    percentChange,
    unitString,
    currencyString,
    hasRates,
    chart,
    lowPriceString,
    highPriceString,
    averagePriceString,
  } = formatMarketRateModelData(rates, mode);

  const {
    header,
    routeLocationName,
    rateBlock,
    rateUnit,
    rateValue,
    rateValueCurrency,
    noRatesText,
    chartWrapper,
    chartBase,
    chartLabel,
    chartLabelIcon,
    stats,
    statsHeader,
    statsBlock,
    statsChange,
    statsChangeContent,
    statsChangeText,
    statsChangeIcon,
    footer,
    footerText,
    poweredByBadge,
  } = styles({ variant });

  return (
    <div>
      {(hasLocations || customLabel) && (
        <header className={header()}>
          <RatesRouteBadge
            mode={mode}
            label={hasLocations ? `${originPortId?.value} to ${destinationPortId?.value}` : customLabel}
          />
          <div className={routeLocationName()}>
            {hasLocations ? `${getLocationName(origin)} to ${getLocationName(destination)}` : customLabel}
          </div>
        </header>
      )}
      <div className={rateBlock()}>
        {hasRates ? (
          <div>
            <Text className={rateUnit()}>Rate {unitString}</Text>
            <Heading as="h4" variant="h3" className={rateValue()}>
              {priceString} <small className={rateValueCurrency()}>{currencyString}</small>
            </Heading>
          </div>
        ) : (
          <div>
            <Heading as="h4" variant="h3" className={noRatesText()}>
              No rates available
            </Heading>
            <Text className="mb-0 text-sm">
              Sometimes rates might not be available due to inconsistent or missing data on that route
            </Text>
          </div>
        )}
      </div>
      {hasRates && (
        <div className={stats()}>
          <Text className={statsHeader()}>Over the last 30 days</Text>
          <div className="flex items-start gap-2 max-md:flex-wrap">
            {percentChange < 0 && (
              <div className={statsChange({ className: 'bg-green-50 text-green-700' })}>
                <div className={statsChangeContent()}>
                  <TrendingDown className={statsChangeIcon({ className: 'text-green-600' })} />
                  {percentString}
                </div>
                <div className={statsChangeText()}>30 day change</div>
              </div>
            )}
            {percentChange > 0 && (
              <div className={statsChange({ className: 'bg-red-50 text-red-700' })}>
                <div className={statsChangeContent()}>
                  <TrendingUp className={statsChangeIcon({ className: 'text-red-600' })} />
                  {percentString}
                </div>
                <div className={statsChangeText()}>30 day change</div>
              </div>
            )}
            {!isLargeTile && (
              <Text className="mb-0 mt-1 whitespace-nowrap text-xs italic text-grey-500">
                {percentChange === 0 && 'No'} change over 30 days
              </Text>
            )}
            {isLargeTile && (
              <>
                <div className={statsBlock()}>
                  <div className="text-base font-medium">{lowPriceString}</div>
                  <div className="text-sm text-grey-500">Low</div>
                </div>
                <div className={statsBlock()}>
                  <div className="text-base font-medium">{highPriceString}</div>
                  <div className="text-sm text-grey-500">High</div>
                </div>
                <div className={statsBlock()}>
                  <div className="text-base font-medium">{averagePriceString}</div>
                  <div className="text-sm text-grey-500">Average</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {shouldShowChart && hasRates && (
        <>
          <div className={chartWrapper()}>
            <Chart
              className={chartBase()}
              data={chart.data}
              type="line"
              chartConfig={generateChartConfig({ keys: ['value'] })}
              settings={{
                axes: {
                  xAxis: {
                    hide: isMiniChart,
                  },
                  yAxis: {
                    hide: isMiniChart,
                    domain: chart.range,
                    tickFormatter: (value) => `$${value}`,
                    width: 48,
                  },
                },
                tooltip: {
                  hide: isMiniChart,
                  valuePrefix: '$',
                },
                grid: {
                  vertical: !isMiniChart,
                  horizontal: !isMiniChart,
                },
                series: {
                  type: 'basis',
                },
              }}
            />
          </div>
          {shouldShowChartLegend && chart.seriesLabels?.size && (
            <div className={chartLabel()}>
              <span className={chartLabelIcon()} />
              <Text className="m-0">
                {chart.seriesLabels.size} ({chart.seriesLabels.range})
              </Text>
            </div>
          )}
        </>
      )}
      {shouldShowFooter && (
        <footer className={footer()}>
          <Text variant="sm" className={footerText({ className: 'mb-2' })}>
            Market rates are the average rate seen in the market based on samples gathered from carriers and freight
            forwarders over a 30-day period.
          </Text>
          <Text variant="sm" className={footerText({ className: 'italic' })}>
            Please note: Market rates are intended to be used as a guide only and may not reflect actual shipping costs.
          </Text>
          <div className={poweredByBadge()}>
            <img src="/assets/fluent-cargo-xeneta.png" alt="Fluent Cargo partnered with Xeneta" />
          </div>
        </footer>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative min-h-[162px] max-w-5xl rounded-lg border border-grey-200 bg-white p-4 text-left leading-normal',
    header: 'mb-3',
    routeLocationName: 'overflow-hidden text-ellipsis whitespace-nowrap text-xs text-grey-500',
    rateBlock: '',
    rateUnit: 'm-0 text-[10px] font-medium uppercase leading-tight',
    rateValue: 'mb-0 font-semibold leading-tight',
    noRatesText: 'mb-1 text-baseSm font-semibold leading-tight',
    rateValueCurrency: 'text-sm',
    stats: 'mt-3',
    statsHeader: 'mb-2 hidden text-sm italic leading-snug text-grey-500',
    statsBlock: 'rounded-md bg-grey-100 px-4 py-1.5 leading-snug',
    statsChange: 'rounded-md px-1.5 py-1 leading-snug',
    statsChangeContent: 'flex items-center gap-1.5 text-sm font-medium',
    statsChangeIcon: 'size-3.5',
    statsChangeText: 'hidden text-sm text-grey-500',
    footer: 'relative mt-4 border-t border-dashed border-grey-300 pt-4',
    footerText: 'm-0 text-xs leading-snug opacity-60',
    poweredByBadge: 'mt-2 h-auto w-10 [&_img]:w-full',
    chartWrapper: 'absolute right-4 top-16 h-14 w-32',
    chartBase: 'aspect-auto h-14 min-h-none',
    chartLabel: 'mt-4 flex items-center gap-2 text-sm text-text-primary',
    chartLabelIcon: 'h-1 w-3 rounded-full bg-[#0795FF]',
  },
  variants: {
    variant: {
      sm: {},
      md: {
        chartWrapper: 'static mt-6 h-32 w-full',
        chartBase: 'h-32',
      },
      lg: {
        base: 'p-6',
        routePortCodes: 'text-baseSm',
        routeLocationName: 'text-sm',
        header: 'mb-5',
        rateBlock: 'flex gap-10',
        rateUnit: 'mb-0 text-xs leading-snug',
        rateValueCurrency: 'text-baseSm',
        rateValue: 'text-lg leading-snug',
        noRatesText: 'text-md',
        chartWrapper: 'static mt-12 h-56 w-full',
        chartBase: 'h-56',
        footer: 'mt-8 border-none pt-0',
        poweredByBadge: 'absolute bottom-1 right-0 mt-0 h-auto w-16 [&_img]:w-full',
        stats: 'mt-5',
        statsHeader: 'block',
        statsChange: 'rounded-md px-4 py-1.5',
        statsChangeIcon: 'size-4',
        statsChangeText: 'block',
        statsChangeContent: 'gap-2 text-base',
      },
    },
  },
});

export { RatesMarketRateDetails };
